import React, { useEffect } from "react";

function buildEmbed(url) {
  if (!url) return null;
  const u = String(url).trim();
  const vimeo = u.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return { type: "iframe", src: `https://player.vimeo.com/video/${vimeo[1]}?autoplay=1&title=0&byline=0&portrait=0` };
  const yt = u.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{11})/);
  if (yt) return { type: "iframe", src: `https://www.youtube.com/embed/${yt[1]}?autoplay=1` };
  const lower = u.split("?")[0].toLowerCase();
  if (lower.endsWith(".mp4") || lower.endsWith(".webm") || lower.endsWith(".mov")) return { type: "video", src: u };
  return { type: "iframe", src: u };
}

/**
 * Generic fullscreen player modal. Preserves the page scroll position because
 * we use a fixed overlay and do not modify body scroll (only lock overflow).
 */
export default function VideoModal({ url, title, subtitle, description, onClose }) {
  const embed = buildEmbed(url);

  useEffect(() => {
    const body = document.body;
    const prevOverflow = body.style.overflow;
    body.style.overflow = "hidden";

    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return (
    <div
      data-testid="video-modal"
      className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-md flex flex-col"
      onClick={onClose}
    >
      <header className="flex items-center justify-between px-5 sm:px-8 py-4 border-b border-white/10 text-white">
        <div onClick={(e) => e.stopPropagation()} className="min-w-0 pr-4">
          {subtitle && (
            <p className="font-mono-jb text-[10px] uppercase tracking-[0.22em] text-[#c4b5fd] truncate">
              {subtitle}
            </p>
          )}
          {title && (
            <h3 className="font-display uppercase text-lg sm:text-xl tracking-tight mt-1 truncate">
              {title}
            </h3>
          )}
        </div>
        <button
          data-testid="video-modal-close"
          aria-label="Закрыть"
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          className="w-11 h-11 flex items-center justify-center border border-white/20 hover:bg-white hover:text-black transition-colors shrink-0"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M2 2 L14 14 M14 2 L2 14" stroke="currentColor" strokeWidth="1.5" />
          </svg>
        </button>
      </header>

      <div
        className="flex-1 flex flex-col lg:flex-row items-stretch gap-4 p-3 sm:p-6 lg:p-10 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex-1 min-h-[40vh] lg:min-h-0 flex items-center justify-center">
          <div className="w-full max-w-[1400px] aspect-video bg-black border border-white/10">
            {embed?.type === "iframe" && (
              <iframe
                data-testid="video-modal-iframe"
                src={embed.src}
                title={title || "video"}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
                frameBorder="0"
              />
            )}
            {embed?.type === "video" && (
              <video data-testid="video-modal-video" src={embed.src} autoPlay controls className="w-full h-full object-contain bg-black" />
            )}
            {!embed && (
              <div className="w-full h-full flex items-center justify-center text-white/50 text-sm font-mono-jb uppercase tracking-[0.22em]">
                Видео скоро будет доступно
              </div>
            )}
          </div>
        </div>

        {description && (
          <aside className="lg:w-[360px] shrink-0 border border-white/10 bg-[#070707] p-6 lg:p-8 text-white/75 text-sm leading-relaxed">
            <p className="font-mono-jb text-[10px] uppercase tracking-[0.22em] text-white/40 mb-3">
              / О проекте
            </p>
            <p>{description}</p>
          </aside>
        )}
      </div>
    </div>
  );
}
